import React, { useMemo, useState, useEffect } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import ConsequenceReveal from '../shared/ConsequenceReveal';
import styles from './LikertSliderEngine.module.css';

const LABELS = ['A', 'B', 'C', 'D', 'E'];
const EMOJIS = ['😟', '🙁', '😐', '🙂', '😄'];
const LABEL_TEXTS = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];

// SVG illustrations for each OCEAN trait
const TraitIllustration = ({ trait, value }) => {
  const normalizedValue = (value - 1) / 4; // 0 to 1
  const accent = '#264653';

  // Openness: Lightbulb with rays
  if (trait?.includes('openness')) {
    return (
      <svg viewBox="0 0 200 140" className={styles.illustrationSvg}>
        <motion.g
          animate={{
            scale: 0.8 + normalizedValue * 0.4,
            opacity: 0.5 + normalizedValue * 0.5
          }}
          transition={{ duration: 0.5 }}
          style={{ transformOrigin: '100px 70px' }}
        >
          {/* Bulb */}
          <path
            d="M100 20 C70 20 50 45 50 70 C50 95 70 100 100 100 C130 100 150 95 150 70 C150 45 130 20 100 20 Z"
            fill="#fef3c7"
            stroke={accent}
            strokeWidth="3"
          />
          {/* Rays - expand with higher value */}
          {normalizedValue > 0.3 && (
            <g>
              <line x1="100" y1="5" x2="100" y2="15" stroke={accent} strokeWidth="3" />
              <line x1="65" y1="15" x2="72" y2="25" stroke={accent} strokeWidth="3" />
              <line x1="135" y1="15" x2="128" y2="25" stroke={accent} strokeWidth="3" />
              {normalizedValue > 0.6 && (
                <>
                  <line x1="40" y1="35" x2="52" y2="42" stroke={accent} strokeWidth="3" />
                  <line x1="160" y1="35" x2="148" y2="42" stroke={accent} strokeWidth="3" />
                </>
              )}
            </g>
          )}
          {/* Base */}
          <rect x="85" y="100" width="30" height="8" rx="2" fill={accent} />
          <rect x="90" y="108" width="20" height="5" rx="2" fill={accent} />
        </motion.g>
      </svg>
    );
  }

  // Conscientiousness: Neat desk with checklist
  if (trait?.includes('conscientiousness')) {
    const checkOpacity = normalizedValue;
    return (
      <svg viewBox="0 0 200 140" className={styles.illustrationSvg}>
        {/* Desk */}
        <rect x="20" y="60" width="160" height="70" rx="4" fill="#fff" stroke={accent} strokeWidth="3" />
        {/* Paper with checklist */}
        <motion.g
          animate={{ x: normalizedValue * 10 }}
          transition={{ duration: 0.5 }}
        >
          <rect x="40" y="40" width="80" height="90" rx="2" fill="#fff" stroke={accent} strokeWidth="2" />
          {/* Checklist lines */}
          <rect x="50" y="60" width="50" height="4" rx="1" fill="#e0e0e0" />
          <rect x="50" y="75" width="40" height="4" rx="1" fill="#e0e0e0" />
          <rect x="50" y="90" width="45" height="4" rx="1" fill="#e0e0e0" />
          {/* Checkmarks that appear with higher values */}
          {checkOpacity > 0.2 && (
            <motion.text
              x="110"
              y="68"
              fontSize="12"
              fill="#2a9d8f"
              initial={{ opacity: 0 }}
              animate={{ opacity: checkOpacity }}
            >
              ✓
            </motion.text>
          )}
          {checkOpacity > 0.5 && (
            <motion.text
              x="95"
              y="83"
              fontSize="12"
              fill="#2a9d8f"
              initial={{ opacity: 0 }}
              animate={{ opacity: checkOpacity }}
            >
              ✓
            </motion.text>
          )}
          {checkOpacity > 0.8 && (
            <motion.text
              x="100"
              y="98"
              fontSize="12"
              fill="#2a9d8f"
              initial={{ opacity: 0 }}
              animate={{ opacity: checkOpacity }}
            >
              ✓
            </motion.text>
          )}
        </motion.g>
        {/* Pen */}
        <rect x="140" y="50" width="8" height="60" rx="1" fill={accent} />
      </svg>
    );
  }

  // Extraversion: Group with connections
  if (trait?.includes('extraversion')) {
    const connectionOpacity = normalizedValue;
    return (
      <svg viewBox="0 0 200 140" className={styles.illustrationSvg}>
        {/* Connection lines */}
        <motion.g animate={{ opacity: connectionOpacity }} transition={{ duration: 0.5 }}>
          <line x1="60" y1="70" x2="140" y2="70" stroke={accent} strokeWidth="2" strokeDasharray="4" />
          <line x1="100" y1="40" x2="60" y2="70" stroke={accent} strokeWidth="2" strokeDasharray="4" />
          <line x1="100" y1="40" x2="140" y2="70" stroke={accent} strokeWidth="2" strokeDasharray="4" />
        </motion.g>
        {/* Three figures */}
        <motion.g animate={{ scale: 0.8 + normalizedValue * 0.3 }} transition={{ duration: 0.5 }}>
          {/* Left */}
          <circle cx="60" cy="70" r="15" fill="#fff" stroke={accent} strokeWidth="2.5" />
          <line x1="60" y1="85" x2="60" y2="110" stroke={accent} strokeWidth="2.5" />
          {/* Center */}
          <circle cx="100" cy="40" r="15" fill="#fff" stroke={accent} strokeWidth="2.5" />
          <line x1="100" y1="55" x2="100" y2="80" stroke={accent} strokeWidth="2.5" />
          {/* Right */}
          <circle cx="140" cy="70" r="15" fill="#fff" stroke={accent} strokeWidth="2.5" />
          <line x1="140" y1="85" x2="140" y2="110" stroke={accent} strokeWidth="2.5" />
        </motion.g>
      </svg>
    );
  }

  // Agreeableness: Handshake / Helping
  if (trait?.includes('agreeableness')) {
    return (
      <svg viewBox="0 0 200 140" className={styles.illustrationSvg}>
        <motion.g
          animate={{
            x: normalizedValue > 0.5 ? 0 : -10,
            opacity: 0.5 + normalizedValue * 0.5
          }}
          transition={{ duration: 0.5 }}
        >
          {/* Two figures approaching */}
          <circle cx="60" cy="70" r="15" fill="#fff" stroke={accent} strokeWidth="2.5" />
          <line x1="60" y1="85" x2="60" y2="110" stroke={accent} strokeWidth="2.5" />
          <line x1="60" y1="95" x2="75" y2="100" stroke={accent} strokeWidth="2.5" />
          <circle cx="140" cy="70" r="15" fill="#fff" stroke={accent} strokeWidth="2.5" />
          <line x1="140" y1="85" x2="140" y2="110" stroke={accent} strokeWidth="2.5" />
          <line x1="140" y1="95" x2="125" y2="100" stroke={accent} strokeWidth="2.5" />
          {/* Connection */}
          {normalizedValue > 0.4 && (
            <motion.path
              d="M75 100 Q 100 95 125 100"
              fill="none"
              stroke="#2a9d8f"
              strokeWidth="3"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5 }}
            />
          )}
        </motion.g>
      </svg>
    );
  }

  // Neuroticism: Weather vane in storm vs calm
  if (trait?.includes('neuroticism')) {
    const calmness = 1 - normalizedValue; // Inverted - higher value = more storm
    return (
      <svg viewBox="0 0 200 140" className={styles.illustrationSvg}>
        {/* Background sky */}
        <motion.rect
          x="0"
          y="0"
          width="200"
          height="140"
          fill={calmness > 0.5 ? "#e0f2fe" : "#fef3c7"}
          animate={{ opacity: 0.3 + calmness * 0.4 }}
          transition={{ duration: 0.5 }}
        />
        {/* Sun or clouds */}
        <motion.g
          animate={{
            opacity: calmness,
            y: calmness > 0.5 ? 0 : -20
          }}
          transition={{ duration: 0.5 }}
        >
          <circle cx="160" cy="30" r="20" fill="#fde68a" />
        </motion.g>
        {/* Storm clouds */}
        <motion.g
          animate={{
            opacity: 1 - calmness,
            x: calmness < 0.5 ? [0, 5, 0] : 0
          }}
          transition={{
            opacity: { duration: 0.5 },
            x: { repeat: Infinity, duration: 0.5 }
          }}
        >
          <path
            d="M120 20 Q 140 5 160 20 Q 180 5 200 20 L 200 50 L 120 50 Z"
            fill="#94a3b8"
          />
        </motion.g>
        {/* Weather vane */}
        <line x1="100" y1="110" x2="100" y2="50" stroke={accent} strokeWidth="3" />
        <polygon points="100,45 110,55 100,50 90,55" fill={accent} />
        <rect x="95" y="110" width="10" height="15" fill={accent} />
      </svg>
    );
  }

  // Default fallback
  return (
    <svg viewBox="0 0 200 140" className={styles.illustrationSvg}>
      <rect x="40" y="40" width="120" height="60" rx="8" fill="#fafafa" stroke={accent} strokeWidth="2" />
      <text x="100" y="75" textAnchor="middle" className={styles.defaultText}>
        {trait || 'Assessment'}
      </text>
    </svg>
  );
};

const LikertSliderEngine = ({ gamifiedQuestion, onAnswer, accent = '#264653' }) => {
  const [value, setValue] = useState(3);
  const [showOutcome, setShowOutcome] = useState(false);
  const [confirmPulse, setConfirmPulse] = useState(false);

  const options = useMemo(() => gamifiedQuestion?.options?.slice(0, 5) || [], [gamifiedQuestion?.options]);
  const selectedLabel = LABELS[value - 1];
  const selectedOption = options.find((option) => option.label === selectedLabel);

  const traitName = String(gamifiedQuestion?.text || '').toLowerCase();

  const handleSliderChange = (newValue) => {
    setValue(newValue);
    setConfirmPulse(true);
    setTimeout(() => setConfirmPulse(false), 300);
  };

  const handleConfirm = () => {
    setShowOutcome(true);
  };

  return (
    <div className={styles.engine} style={{ '--accent': accent }}>
      {/* SCENE AREA - Top */}
      <div className={styles.sceneArea}>
        <motion.div
          className={styles.illustration}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <TraitIllustration trait={traitName} value={value} />
        </motion.div>
      </div>

      {/* QUESTION TEXT */}
      <div className={styles.questionText}>
        {gamifiedQuestion?.situation}
      </div>

      {/* SLIDER AREA */}
      <div className={styles.sliderArea}>
        {/* Track with stops */}
        <div className={styles.trackContainer}>
          <motion.div
            className={styles.track}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            style={{ transformOrigin: 'left' }}
          >
            <div className={styles.trackFill} style={{ width: `${((value - 1) / 4) * 100}%` }} />
          </motion.div>

          {/* Stop circles */}
          <div className={styles.stops}>
            {[1, 2, 3, 4, 5].map((stop, index) => (
              <motion.button
                key={stop}
                className={`${styles.stop} ${value === stop ? styles.activeStop : ''}`}
                onClick={() => handleSliderChange(stop)}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4 + index * 0.1, type: 'spring', stiffness: 200 }}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <span className={styles.emoji}>{EMOJIS[index]}</span>
                <span className={styles.stopLabel}>{LABEL_TEXTS[index]}</span>
              </motion.button>
            ))}
          </div>

          {/* Animated thumb */}
          <motion.div
            className={styles.thumb}
            animate={{
              left: `${((value - 1) / 4) * 100}%`,
              scale: confirmPulse ? 1.2 : 1
            }}
            transition={{ type: 'spring', stiffness: 150, damping: 20 }}
          >
            <span className={styles.thumbLabel}>{selectedLabel}</span>
          </motion.div>
        </div>

        {/* Confirm button */}
        <motion.button
          type="button"
          className={styles.confirmBtn}
          onClick={handleConfirm}
          animate={{
            scale: confirmPulse ? [1, 1.05, 1] : 1,
            boxShadow: confirmPulse
              ? ['0 0 0 0 rgba(230, 57, 70, 0.4)', '0 0 0 10px rgba(230, 57, 70, 0)', '0 0 0 0 rgba(230, 57, 70, 0)']
              : 'none'
          }}
          transition={{ duration: 0.3 }}
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
        >
          Confirm Response {selectedLabel}
        </motion.button>
      </div>

      {/* Narration */}
      <p className={styles.narration}>{gamifiedQuestion?.narration}</p>

      <ConsequenceReveal
        visible={showOutcome}
        tone={selectedLabel === 'A' || selectedLabel === 'B' ? 'warning' : selectedLabel === 'C' ? 'neutral' : 'success'}
        message={selectedOption?.consequence || 'Response saved.'}
        duration={1500}
        onDone={() => {
          setShowOutcome(false);
          onAnswer(selectedLabel);
        }}
      />
    </div>
  );
};

export default LikertSliderEngine;
